import React from 'react';
import './BasicInformation.css';

/* =========================================================
   PROPERTY TYPES
========================================================= */

const PROPERTY_TYPES = [
    'Hotel',
    'Resort',
    'Villa',
    'Homestay',
    'Cottage',
    'Hostel',
    'Camping',
    'Tent',
    'Campsite',
    'Cabin',
    'Treehouse',
    'Farmstay',
    'Guesthouse',
    'House',
    'Apartment',
    'Other'
];

/* =========================================================
   PROPERTY CATEGORIES
   Category options depend on selected property type.
========================================================= */

const PROPERTY_CATEGORIES = {
    hotel: ['Entire Property', 'Private Room', 'Shared Room', 'Dormitory'],

    resort: ['Entire Property', 'Private Room', 'Villa', 'Cottage'],

    villa: ['Entire Property', 'Private Room'],

    homestay: ['Entire Property', 'Private Room', 'Shared Room'],

    cottage: ['Entire Property', 'Private Room'],

    hostel: ['Entire Property', 'Shared Room', 'Dormitory'],

    camping: ['Campsite', 'Tent'],

    tent: ['Tent', 'Campsite'],

    campsite: ['Campsite', 'Tent'],

    cabin: ['Entire Property', 'Private Room'],

    treehouse: ['Entire Property', 'Private Room'],

    farmstay: ['Entire Property', 'Private Room', 'Shared Room'],

    guesthouse: ['Entire Property', 'Private Room', 'Shared Room'],

    house: ['Entire Property', 'Private Room', 'Shared Room'],

    apartment: ['Entire Property', 'Private Room'],

    other: ['Other']
};

/* =========================================================
   LANGUAGES
========================================================= */

const LANGUAGES = ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu', 'Malayalam', 'Marathi', 'Bengali'];

/* =========================================================
   HELPERS
========================================================= */

const formatValue = (value) => {
    return value.toLowerCase().replace(/\s+/g, '_');
};

/* =========================================================
   COMPONENT
========================================================= */

const BasicInformation = ({ formData, updateField, errors = {}, canEdit = true }) => {
    /* =====================================================
       CURRENT PROPERTY TYPE
    ===================================================== */

    const selectedPropertyType = formData.propertyType || '';

    /* =====================================================
       AVAILABLE CATEGORIES
    ===================================================== */

    const availableCategories = PROPERTY_CATEGORIES[selectedPropertyType] || [];

    /*
     * The category is cleared only when the user changes the property type
     * (handlePropertyTypeChange below) — not on load, so a saved category
     * that isn't in this type's list (older records) is kept and shown.
     */
    const savedCategoryMissing =
        Boolean(formData.propertyCategory) &&
        !availableCategories.some((category) => formatValue(category) === formData.propertyCategory);

    const savedTypeMissing =
        Boolean(selectedPropertyType) && !PROPERTY_TYPES.some((type) => formatValue(type) === selectedPropertyType);

    const labelOf = (value) => value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    /* =====================================================
       HIGHLIGHTS
    ===================================================== */

    const handleHighlightsChange = (event) => {
        const value = event.target.value;

        const highlights = value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);

        updateField('highlights', highlights);
    };

    /* =====================================================
       LANGUAGES
    ===================================================== */

    const handleLanguageToggle = (language) => {
        const currentLanguages = formData.languages || [];

        const isSelected = currentLanguages.includes(language);

        const updatedLanguages = isSelected
            ? currentLanguages.filter((item) => item !== language)
            : [...currentLanguages, language];

        updateField('languages', updatedLanguages);
    };

    /* =====================================================
       PROPERTY TYPE CHANGE
    ===================================================== */

    const handlePropertyTypeChange = (event) => {
        const propertyType = event.target.value;

        updateField('propertyType', propertyType);

        /*
         * Always clear category when property type
         * changes.
         *
         * (Only here — a saved category is never
         * cleared just by opening the form.)
         */

        updateField('propertyCategory', '');
    };

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div className='basic-information'>
            {/* =====================================================
                PROPERTY IDENTITY
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Property Information</h3>

                        <p>Tell travelers about your accommodation.</p>
                    </div>
                </div>

                <div className='form-grid'>
                    {/* =================================================
                        PROPERTY NAME
                    ================================================= */}

                    <div className='form-field full-width'>
                        <label htmlFor='title'>
                            Property Name
                            <span className='required'>*</span>
                        </label>

                        <input
                            id='title'
                            type='text'
                            value={formData.title || ''}
                            placeholder='e.g. Coorg Forest Escape'
                            disabled={!canEdit}
                            onChange={(e) => updateField('title', e.target.value)}
                        />

                        {errors.title && <span className='field-error'>{errors.title}</span>}
                    </div>

                    {/* =================================================
                        SLUG
                    ================================================= */}

                    <div className='form-field'>
                        <label htmlFor='slug'>Slug</label>

                        <input
                            id='slug'
                            type='text'
                            value={formData.slug || ''}
                            placeholder='coorg-forest-escape'
                            disabled={!canEdit}
                            onChange={(e) => updateField('slug', e.target.value)}
                        />

                        <small>URL-friendly name. Leave empty to generate automatically.</small>
                    </div>

                    {/* =================================================
                        PROPERTY TYPE
                    ================================================= */}

                    <div className='form-field'>
                        <label htmlFor='propertyType'>
                            Property Type
                            <span className='required'>*</span>
                        </label>

                        <select
                            id='propertyType'
                            value={formData.propertyType || ''}
                            disabled={!canEdit}
                            onChange={handlePropertyTypeChange}
                        >
                            <option value=''>Select property type</option>

                            {savedTypeMissing && <option value={selectedPropertyType}>{labelOf(selectedPropertyType)}</option>}

                            {PROPERTY_TYPES.map((type) => (
                                <option key={type} value={formatValue(type)}>
                                    {type}
                                </option>
                            ))}
                        </select>

                        {errors.propertyType && <span className='field-error'>{errors.propertyType}</span>}
                    </div>

                    {/* =================================================
                        PROPERTY CATEGORY
                    ================================================= */}

                    <div className='form-field'>
                        <label htmlFor='propertyCategory'>
                            Property Category
                            <span className='required'>*</span>
                        </label>

                        <select
                            id='propertyCategory'
                            value={formData.propertyCategory || ''}
                            disabled={!canEdit || !selectedPropertyType}
                            onChange={(e) => updateField('propertyCategory', e.target.value)}
                        >
                            {!selectedPropertyType ? (
                                <option value=''>Select property type first</option>
                            ) : (
                                <>
                                    <option value=''>Select category</option>

                                    {savedCategoryMissing && (
                                        <option value={formData.propertyCategory}>
                                            {labelOf(formData.propertyCategory)}
                                        </option>
                                    )}

                                    {availableCategories.map((category) => (
                                        <option key={category} value={formatValue(category)}>
                                            {category}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>

                        {!selectedPropertyType && (
                            <small>Select a property type to see the available categories.</small>
                        )}

                        {errors.propertyCategory && <span className='field-error'>{errors.propertyCategory}</span>}
                    </div>
                </div>
            </section>

            {/* =====================================================
                DESCRIPTION
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>About Your Property</h3>

                        <p>Give travelers a clear understanding of what makes this property special.</p>
                    </div>
                </div>

                <div className='form-field'>
                    <label htmlFor='overview'>Short Overview</label>

                    <input
                        id='overview'
                        type='text'
                        maxLength={180}
                        value={formData.overview || ''}
                        placeholder='A beautiful nature stay surrounded by forests and hills'
                        disabled={!canEdit}
                        onChange={(e) => updateField('overview', e.target.value)}
                    />

                    <div className='character-count'>
                        {(formData.overview || '').length}
                        /180
                    </div>
                </div>

                <div className='form-field'>
                    <label htmlFor='description'>Detailed Description</label>

                    <textarea
                        id='description'
                        rows={7}
                        value={formData.description || ''}
                        placeholder='Describe the property, surroundings, experience, accommodation and what guests can expect...'
                        disabled={!canEdit}
                        onChange={(e) => updateField('description', e.target.value)}
                    />
                </div>
            </section>

            {/* =====================================================
                PROPERTY CAPACITY
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Property Capacity</h3>

                        <p>General capacity information for the property.</p>
                    </div>
                </div>

                <div className='form-grid four-columns'>
                    {/* MAX GUESTS */}

                    <div className='form-field'>
                        <label htmlFor='maxGuests'>Maximum Guests</label>

                        <input
                            id='maxGuests'
                            type='number'
                            min='0'
                            value={formData.maxGuests || ''}
                            placeholder='0'
                            disabled={!canEdit}
                            onChange={(e) => updateField('maxGuests', Number(e.target.value))}
                        />
                    </div>

                    {/* BEDROOMS */}

                    <div className='form-field'>
                        <label htmlFor='totalBedrooms'>Total Bedrooms</label>

                        <input
                            id='totalBedrooms'
                            type='number'
                            min='0'
                            value={formData.totalBedrooms || ''}
                            placeholder='0'
                            disabled={!canEdit}
                            onChange={(e) => updateField('totalBedrooms', Number(e.target.value))}
                        />
                    </div>

                    {/* BATHROOMS */}

                    <div className='form-field'>
                        <label htmlFor='totalBathrooms'>Total Bathrooms</label>

                        <input
                            id='totalBathrooms'
                            type='number'
                            min='0'
                            value={formData.totalBathrooms || ''}
                            placeholder='0'
                            disabled={!canEdit}
                            onChange={(e) => updateField('totalBathrooms', Number(e.target.value))}
                        />
                    </div>

                    {/* UNITS */}

                    <div className='form-field'>
                        <label htmlFor='totalUnits'>Total Units</label>

                        <input
                            id='totalUnits'
                            type='number'
                            min='0'
                            value={formData.totalUnits || ''}
                            placeholder='0'
                            disabled={!canEdit}
                            onChange={(e) => updateField('totalUnits', Number(e.target.value))}
                        />
                    </div>
                </div>

                {/* ENTIRE PROPERTY */}

                <div className='toggle-row'>
                    <div>
                        <strong>Entire property available</strong>

                        <p>Guests can book the entire property instead of individual units.</p>
                    </div>

                    <button
                        type='button'
                        className={`toggle ${formData.entirePropertyAvailable ? 'active' : ''}`}
                        disabled={!canEdit}
                        onClick={() => updateField('entirePropertyAvailable', !formData.entirePropertyAvailable)}
                        aria-label='Toggle entire property availability'
                    >
                        <span />
                    </button>
                </div>
            </section>

            {/* =====================================================
                HIGHLIGHTS
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Property Highlights</h3>

                        <p>Add short highlights that travelers can quickly understand.</p>
                    </div>
                </div>

                <div className='form-field'>
                    <label htmlFor='highlights'>Highlights</label>

                    <input
                        id='highlights'
                        type='text'
                        value={(formData.highlights || []).join(', ')}
                        placeholder='Mountain View, Campfire, Swimming Pool, Breakfast'
                        disabled={!canEdit}
                        onChange={handleHighlightsChange}
                    />

                    <small>Separate multiple highlights with commas.</small>
                </div>

                {formData.highlights?.length > 0 && (
                    <div className='tag-preview'>
                        {formData.highlights.map((highlight, index) => (
                            <span className='preview-tag' key={`${highlight}-${index}`}>
                                {highlight}
                            </span>
                        ))}
                    </div>
                )}
            </section>

            {/* =====================================================
                LANGUAGES
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Languages</h3>

                        <p>Select the languages spoken by the property staff or host.</p>
                    </div>
                </div>

                <div className='form-field'>
                    <label>Languages Spoken</label>

                    <div className='language-selection'>
                        {LANGUAGES.map((language) => {
                            const isSelected = (formData.languages || []).includes(language);

                            return (
                                <button
                                    key={language}
                                    type='button'
                                    className={`language-option ${isSelected ? 'selected' : ''}`}
                                    disabled={!canEdit}
                                    onClick={() => handleLanguageToggle(language)}
                                >
                                    <span className='language-check'>{isSelected ? '✓' : ''}</span>

                                    <span>{language}</span>
                                </button>
                            );
                        })}
                    </div>

                    <small>Select all languages spoken by the host or property staff.</small>
                </div>
            </section>
        </div>
    );
};

export default BasicInformation;
